import { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PlusIcon,
  XMarkIcon,
  TagIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  ExclamationTriangleIcon,
  TrashIcon,
  PhotoIcon
} from '@heroicons/react/24/outline';
import { disciplineService } from '../../../services/disciplineService';
import { mentionTypesService } from '../../../services/mentionTypesService';
import { recognitionService } from '../../../services/recognitionService';
import { useNotifications } from '../../../components/notifications/NotificationSystem';
import { useConfirmDialog } from '../../../hooks/useConfirmDialog';
import { CSS_CLASSES } from '../../../utils/constants';
import { ConfirmDialog } from '../../../components/shared/UIComponents';
import StandardPage from '../../../components/loading/StandardPage';
import OffencesManagerSkeleton from '../../../components/skeletons/OffencesManagerSkeleton';
import { useAuth } from '../../../features/auth/hooks/useAuth';
import { useSchoolFirestore } from '../../../hooks/useSchoolFirestore';
import { useSchool } from '../../../contexts/SchoolContext';
import { useDomainStore } from '@/stores/domainStore';
import CategoryTag from './CategoryTag';
import OffenceTag from './OffenceTag';
import ValueCard from './ValueCard';
import IconPicker from '../../../components/ui/IconPicker';
import ColorPicker from '../../../components/ui/ColorPicker';
import { getIconByName } from '../../../utils/iconMapping';
import EmbeddedToolbar from '../../../components/layout/substratePanels/EmbeddedToolbar';
import { commitProtectedDomainDefinitionConfiguration } from '@/services/protectedDomainDefinitionConfiguration';
import PhaseApplicabilityField from '@/features/phases/components/PhaseApplicabilityField';
import usePhaseConfigurationView from '@/features/phases/usePhaseConfigurationView';
import PhaseScopeSelector from '@/components/ui/PhaseScopeSelector';
import { normalizePhaseApplicability } from '@/services/domain/phaseApplicability';
import { db, doc, getDoc } from '@/firebase';
import { isRecognitionDefinition } from '@/services/domain/recognitionDefinition';

// Severity level configuration
const SEVERITY_LEVELS = [
  {
    id: 1,
    name: 'Routine',
    description: 'Minor infractions'
  },
  {
    id: 2,
    name: 'Moderate',
    description: 'Detention required'
  },
  {
    id: 3,
    name: 'Serious',
    description: 'Saturday detention'
  },
  {
    id: 4,
    name: 'Critical',
    description: 'Severe consequences'
  }
];

// Recognition level configuration (with points)
const RECOGNITION_LEVELS = [
  {
    id: 1,
    name: 'Routine',
    description: 'Minor recognitions',
    points: 1
  },
  {
    id: 2,
    name: 'Noteworthy',
    description: 'Meaningful actions',
    points: 2
  },
  {
    id: 3,
    name: 'Outstanding',
    description: 'Significant achievements',
    points: 3
  },
  {
    id: 4,
    name: 'Exceptional',
    description: 'Exemplary behavior',
    points: 5
  }
];

// Note: Categories are user-created, not predefined

export default function OffencesManager({ embedded = false, domain: domainProp = null } = {}) {
  const modeLocked = ['discipline', 'recognition'].includes(domainProp?.id);
  // A selected protected Domain is authoritative. Standalone use still keeps
  // the operator's existing local toggle preference.
  const [isRecognitionMode, setIsRecognitionMode] = useState(
    domainProp?.id === 'recognition'
      || (!modeLocked && localStorage.getItem('offencesManagerRecognitionMode') === 'true')
  );

  const [offences, setOffences] = useState([]);
  const [recognitions, setRecognitions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [availableValues, setAvailableValues] = useState([]);
  const [mentionTypes, setMentionTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showOffenceModal, setShowOffenceModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showValueSelector, setShowValueSelector] = useState(false);
  const [editingOffence, setEditingOffence] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const { showError, showSuccess, showWarning } = useNotifications();
  const { user } = useAuth();
  const { confirmDialog, showConfirm } = useConfirmDialog();
  const { schoolId } = useSchool();
  const { activeDomain: storeActiveDomain, domains } = useDomainStore();
  // `domain` prop (panel-local setup selection) overrides the workspace
  // activeDomain so the manager can target a domain that isn't currently active.
  const activeDomain = domainProp ?? storeActiveDomain;

  // Custom (non-protected) domain scoping: filter offences/categories to this domain only,
  // and stamp domainId on writes. Legacy discipline data has no domainId and stays in the
  // protected discipline domain.
  const isCustomDomain = embedded && activeDomain && !['discipline', 'recognition'].includes(activeDomain.id);
  const viewDomain = domainProp ?? (isCustomDomain ? activeDomain
    : domains?.find(domain => domain.id === (isRecognitionMode ? 'recognition' : 'discipline')));
  const { filter: filterDefinitions } = usePhaseConfigurationView(viewDomain);
  const activeDomainId = activeDomain?.id ?? null;
  const [savingDefinition, setSavingDefinition] = useState(false);
  const [definitionError, setDefinitionError] = useState(null);
  const definitionCreateId = useRef(null);
  const saveOffenceDefinition = (operation, definitionId, extra = {}) =>
    commitProtectedDomainDefinitionConfiguration({ schoolId, domainId: 'discipline', kind: 'offence',
      operation, definitionId, ...extra });
  const saveRecognitionDefinition = (operation, definitionId, extra = {}) =>
    commitProtectedDomainDefinitionConfiguration({ schoolId, domainId: 'recognition', kind: 'recognition-definition',
      operation, definitionId, ...extra });

  // A draft belongs to the school and configuration panel where it was opened.
  useEffect(() => {
    setShowOffenceModal(false);
    setEditingOffence(null);
    setEditingRecognition(null);
    setHasUnsavedDefinitionChanges(false);
    setDefinitionError(null);
    definitionCreateId.current = null;
  }, [schoolId, activeDomainId, isRecognitionMode]);

  // School-scoped CRUD operations
  const { create: createWithSchool, update: updateWithSchool } = useSchoolFirestore();

  // Form states - persist draft data
  const [offenceForm, setOffenceForm] = useState({
    name: '',
    description: '',
    alternateNames: '',
    category: '',
    levels: [],
    valueIds: [],
    mentionType: 'mention',
    mentionTypeId: null
  });
  const [hasUnsavedDefinitionChanges, setHasUnsavedDefinitionChanges] = useState(false);

  // Good deed form state (for recognition mode)
  const [recognitionForm, setRecognitionForm] = useState({
    name: '',
    description: '',
    levels: [],
    valueIds: []
  });
  const [editingRecognition, setEditingRecognition] = useState(null);

  const [categoryForm, setCategoryForm] = useState({
    name: '',
    icon: 'TagIcon',
    color: '#3B82F6'
  });
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);

  // Refs for click-outside detection
  const valueSelectorRef = useRef();

  // Toggle handler
  useEffect(() => {
    if (modeLocked) setIsRecognitionMode(domainProp.id === 'recognition');
  }, [domainProp?.id, modeLocked]);

  const handleToggleMode = (checked) => {
    if (modeLocked) return;
    setIsRecognitionMode(checked);
    localStorage.setItem('offencesManagerRecognitionMode', checked.toString());
  };

  // Load data on component mount and when mode changes
  useEffect(() => {
    loadData();
  }, [schoolId, isRecognitionMode]);

  // Close value selector when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (valueSelectorRef.current && !valueSelectorRef.current.contains(event.target)) {
        setShowValueSelector(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadData = async () => {
    if (!schoolId) return;

    try {
      setLoading(true);

      if (isRecognitionMode) {
        // Clear discipline mode data
        setOffences([]);
        setCategories([]);
        setMentionTypes([]);

        // Load recognitions and values for recognition mode
        const [recognitionsData, valuesData] = await Promise.all([
          recognitionService.getRecognitions(schoolId),
          disciplineService.getValues(schoolId)
        ]);
        setRecognitions(recognitionsData);
        setAvailableValues(valuesData);
      } else {
        // Clear recognition mode data
        setRecognitions([]);

        // Load offences for discipline mode
        const [offencesData, categoriesData, valuesData, mentionTypesData] = await Promise.all([
          disciplineService.getOffences(schoolId),
          disciplineService.getDisciplineCategories(schoolId),
          disciplineService.getValues(schoolId),
          mentionTypesService.getAll(schoolId)
        ]);
        setOffences(offencesData);
        setCategories(categoriesData);
        setAvailableValues(valuesData);
        setMentionTypes(mentionTypesData.filter(type => type.mode !== 'recognition' && type.active !== false && !type.retired));
      }
    } catch (error) {
      console.error('Error loading data:', error);
      showError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadOffences = async () => {
    if (!schoolId) return;

    try {
      const offencesData = await disciplineService.getOffences(schoolId);
      setOffences(offencesData);
    } catch (error) {
      console.error('Error loading offences:', error);
      showError('Failed to load offences');
    }
  };

  const loadCategories = async () => {
    if (!schoolId) return;

    try {
      const categoriesData = await disciplineService.getDisciplineCategories(schoolId);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading categories:', error);
      showError('Failed to load categories');
    }
  };

  // Domain-scoped category list for sidebar/filters
  const visibleCategories = useMemo(() => {
    if (isCustomDomain) return categories.filter((c) => c.domainId === activeDomainId);
    if (embedded) return categories.filter((c) => !c.domainId);
    return categories;
  }, [categories, isCustomDomain, activeDomainId, embedded]);

  const phaseOffences = useMemo(() => filterDefinitions(offences).filter(offence => isCustomDomain
    ? offence.domainId === activeDomainId : !offence.domainId || offence.domainId === 'discipline'),
  [offences, filterDefinitions, isCustomDomain, activeDomainId]);

  // Filter offences based on search and category, after Domain and phase scope.
  const filteredOffences = useMemo(() => {
    let filtered = phaseOffences;

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(offence =>
        offence.name.toLowerCase().includes(query) ||
        offence.description?.toLowerCase().includes(query) ||
        offence.alternateNames?.some(alt => alt.toLowerCase().includes(query))
      );
    }

    // Filter by selected category
    if (selectedCategory) {
      if (selectedCategory === 'all') {
        // Show all offences - no filter needed
      } else if (selectedCategory === 'uncategorized') {
        // Show only uncategorized offences
        filtered = filtered.filter(offence => !offence.categoryId);
      } else {
        // Filter by specific category
        filtered = filtered.filter(offence => offence.categoryId === selectedCategory);
      }
    }

    return filtered;
  }, [phaseOffences, searchQuery, selectedCategory]);

  // Get offences by level for display
  const getOffencesByLevel = (level) => {
    return filteredOffences.filter(offence =>
      offence.levels && offence.levels.includes(level)
    );
  };

  // Get recognitions by level for display
  const getRecognitionsByLevel = (level) => {
    return filterDefinitions(recognitions).filter(deed =>
      (deed.levels || [deed.level]).includes(level)
    );
  };

  // Get category for offence
  const getCategoryForOffence = (categoryId) => {
    return categories.find(cat => cat.id === categoryId);
  };

  // Handle offence form submission
  const handleOffenceSubmit = async (e) => {
    e.preventDefault();
    if (savingDefinition) return;

    // Validation
    if (!offenceForm.name.trim()) {
      // Match the modal's noun: custom domains show "Create Event", so the
      // validation must say "Event name…" not "Offence name…". Discipline
      // (not custom) keeps the original copy byte-identical.
      const eventNoun = isCustomDomain
        ? (activeDomain?.terminology?.event?.label || 'Event')
        : 'Offence';
      showError(`${eventNoun} name is required`);
      return;
    }

    if (!isCustomDomain && offenceForm.levels.length === 0) {
      showError('Please select at least one severity level');
      return;
    }

    setSavingDefinition(true);
    setDefinitionError(null);
    try {
      const effectiveLevels = isCustomDomain && offenceForm.levels.length === 0 ? [1] : offenceForm.levels;
      const offenceData = {
        name: offenceForm.name.trim(),
        description: offenceForm.description.trim(),
        alternateNames: offenceForm.alternateNames
          ? offenceForm.alternateNames.split(',').map(name => name.trim()).filter(Boolean)
          : [],
        categoryId: offenceForm.category || null,  // Note: form field is 'category' but we save as 'categoryId'
        level: effectiveLevels[0] || 1, // Save primary level (first selected)
        levels: effectiveLevels, // Keep array for multi-level support
        valueIds: offenceForm.valueIds, // Store value IDs
        mentionType: offenceForm.mentionType || 'mention',
        mentionTypeId: offenceForm.mentionTypeId || null,
        active: true,
        ...(isCustomDomain ? { createdBy: user?.uid || 'system', ...(!editingOffence ? { domainId: activeDomainId } : {}) }
          : { phaseApplicability: normalizePhaseApplicability(offenceForm.phaseApplicability) })
      };

      if (isCustomDomain) {
        if (editingOffence) await updateWithSchool('offences', editingOffence.id, offenceData);
        else await createWithSchool('offences', offenceData);
      } else {
        definitionCreateId.current ||= crypto.randomUUID();
        await saveOffenceDefinition(editingOffence ? 'update' : 'create', editingOffence?.id || definitionCreateId.current, {
          configuration: offenceData,
          ...(editingOffence ? { expectedRevision: editingOffence.configurationRevision || 0 } : {}),
        });
      }
      showSuccess(`Offence ${editingOffence ? 'updated' : 'created'} successfully`);
      definitionCreateId.current = null;

      // Clear draft state and reload offences
      setHasUnsavedDefinitionChanges(false);
      await loadOffences();

      // Close modal and reset form
      setShowOffenceModal(false);
      setEditingOffence(null);
      setOffenceForm({
        name: '',
        description: '',
        alternateNames: '',
        category: '',
        levels: [],
        valueIds: [],
        mentionType: 'mention',
        mentionTypeId: null
      });
    } catch (error) {
      console.error('Error saving offence:', error);
      setDefinitionError({ message: error.message || 'Failed to save offence',
        stale: ['aborted', 'functions/aborted'].includes(error.code) });
    } finally { setSavingDefinition(false); }
  };

  // Shared Recognition definitions use the same trusted configuration boundary.
  const handleRecognitionSubmit = async (e) => {
    e.preventDefault();
    if (savingDefinition) return;
    if (!recognitionForm.name.trim()) { showError('Recognition name is required'); return; }
    if (!recognitionForm.levels.length) { showError('Please select at least one significance level'); return; }
    setSavingDefinition(true);
    setDefinitionError(null);
    try {
      definitionCreateId.current ||= crypto.randomUUID();
      await saveRecognitionDefinition(editingRecognition ? 'update' : 'create', editingRecognition?.id || definitionCreateId.current, {
        configuration: { name: recognitionForm.name.trim(), description: recognitionForm.description.trim(),
          levels: recognitionForm.levels, valueIds: recognitionForm.valueIds, active: true,
          phaseApplicability: normalizePhaseApplicability(recognitionForm.phaseApplicability) },
        ...(editingRecognition ? { expectedRevision: editingRecognition.configurationRevision || 0 } : {}),
      });
      showSuccess(`Recognition ${editingRecognition ? 'updated' : 'created'} successfully`);
      setRecognitions(await recognitionService.getRecognitions(schoolId));
      setHasUnsavedDefinitionChanges(false);
      definitionCreateId.current = null;
      setShowOffenceModal(false);
      setEditingRecognition(null);
      setRecognitionForm({ name: '', description: '', levels: [], valueIds: [] });
    } catch (error) {
      setDefinitionError({ message: error.message || 'Failed to save recognition',
        stale: ['aborted', 'functions/aborted'].includes(error.code) });
    } finally { setSavingDefinition(false); }
  };

  // Handle category form submission
  const handleCategorySubmit = async (e) => {
    e.preventDefault();

    if (!categoryForm.name.trim()) {
      showWarning('Category name is required');
      return;
    }

    try {
      const categoryData = {
        name: categoryForm.name.trim(),
        icon: categoryForm.icon,
        color: categoryForm.color,
        createdBy: user?.uid || 'system'
      };

      if (editingCategory) {
        // Preserve the active field when updating
        await updateWithSchool('disciplineCategories', editingCategory.id, {
          ...categoryData,
          active: editingCategory.active !== false // Preserve active status
        });
        showSuccess('Category updated successfully');
      } else {
        await createWithSchool('disciplineCategories', {
          ...categoryData,
          ...(isCustomDomain ? { domainId: activeDomainId } : {})
        });
        showSuccess('Category created successfully');
      }

      // Reload both categories and offences to ensure consistency
      await loadData();
      closeCategoryModal();
    } catch (error) {
      console.error('Error saving category:', error);
      showError('Failed to save category');
    }
  };

  // Handle category deletion
  const handleCategoryDelete = async (category) => {
    const confirmed = await showConfirm({
      title: 'Delete Category?',
      message: `Delete "${category.name}" category? Offences using this category will become uncategorized.`,
      variant: 'warning',
      confirmText: 'Delete'
    });
    if (!confirmed) {
      return;
    }

    try {
      await disciplineService.deleteDisciplineCategory(category.id, schoolId);
      showSuccess('Category deleted successfully');
      await loadCategories();
      closeCategoryModal();
    } catch (error) {
      console.error('Error deleting category:', error);
      showError('Failed to delete category');
    }
  };

  // Keep the shared offence and its historical identity when it is retired.
  const handleOffenceDelete = async (offence) => {
    if (savingDefinition) return;
    const confirmed = await showConfirm({
      title: isCustomDomain ? 'Delete Event?' : 'Retire Offence?',
      message: isCustomDomain ? `Delete "${offence.name}"?` : `Retire "${offence.name}"? Its history will remain.`,
      variant: 'danger', confirmText: isCustomDomain ? 'Delete' : 'Retire'
    });
    if (!confirmed) return;
    setSavingDefinition(true);
    setDefinitionError(null);
    try {
      if (isCustomDomain) await disciplineService.deleteOffence(offence.id, schoolId);
      else await saveOffenceDefinition('retire', offence.id, { expectedRevision: offence.configurationRevision || 0 });
      showSuccess(isCustomDomain ? 'Event deleted' : 'Offence retired');
      await loadOffences();
      setShowOffenceModal(false);
      setEditingOffence(null);
    } catch (error) {
      setDefinitionError({ message: error.message || 'Failed to retire offence',
        stale: ['aborted', 'functions/aborted'].includes(error.code) });
    } finally { setSavingDefinition(false); }
  };

  const handleRecognitionDelete = async (recognition) => {
    if (savingDefinition) return;
    const confirmed = await showConfirm({ title: 'Retire Recognition?',
      message: `Retire "${recognition.name}"? Its history will remain.`, variant: 'danger', confirmText: 'Retire' });
    if (!confirmed) return;
    setSavingDefinition(true);
    setDefinitionError(null);
    try {
      await saveRecognitionDefinition('retire', recognition.id, { expectedRevision: recognition.configurationRevision || 0 });
      setRecognitions(await recognitionService.getRecognitions(schoolId));
      showSuccess('Recognition retired');
      setShowOffenceModal(false);
      setEditingRecognition(null);
    } catch (error) {
      setDefinitionError({ message: error.message || 'Failed to retire recognition',
        stale: ['aborted', 'functions/aborted'].includes(error.code) });
    } finally { setSavingDefinition(false); }
  };

  // Modal handlers
  const openOffenceModal = (item = null) => {
    setDefinitionError(null);
    if (isRecognitionMode) {
      if (item) {
        setEditingRecognition(item);
        setRecognitionForm({ name: item.name || '', description: item.description || '',
          levels: item.levels || (item.level ? [item.level] : []), valueIds: item.valueIds || [],
          phaseApplicability: item.phaseApplicability });
        setHasUnsavedDefinitionChanges(false);
      } else {
        setEditingRecognition(null);
        if (!hasUnsavedDefinitionChanges) {
          definitionCreateId.current = crypto.randomUUID();
          setRecognitionForm({ name: '', description: '', levels: [], valueIds: [] });
        }
      }
    } else {
      // Offence modal
      if (item) {
        // Editing existing offence
        setEditingOffence(item);
        setOffenceForm({
          name: item.name || '',
          description: item.description || '',
          alternateNames: item.alternateNames ? item.alternateNames.join(', ') : '',
          category: item.categoryId || '',
          levels: item.levels || (item.level ? [item.level] : []),
          valueIds: item.valueIds || [],
          phaseApplicability: item.phaseApplicability,
          mentionType: item.mentionType || 'mention',
          mentionTypeId: item.mentionTypeId || null
        });
        setHasUnsavedDefinitionChanges(false);
      } else {
        // Creating new offence
        setEditingOffence(null);
        if (!hasUnsavedDefinitionChanges) {
          definitionCreateId.current = crypto.randomUUID();
          const defaultType = mentionTypes.find(type => type.isDefault) || mentionTypes[0];
          setOffenceForm({
            name: '',
            description: '',
            alternateNames: '',
            category: '',
            levels: [],
            valueIds: [],
            mentionType: defaultType?.value || 'mention',
            mentionTypeId: defaultType?.id || null
          });
        }
      }
    }
    setShowOffenceModal(true);
  };

  const closeOffenceModal = async () => {
    if (savingDefinition) return;
    const form = isRecognitionMode ? recognitionForm : offenceForm;
    const hasChanges = form.name.trim() !== '' || form.description.trim() !== '' || form.levels.length > 0 ||
      form.valueIds.length > 0 || form.phaseApplicability?.mode === 'selected-phases' ||
      (!isRecognitionMode && (offenceForm.alternateNames.trim() !== '' || offenceForm.category !== ''));

    if (hasChanges && !(isRecognitionMode ? editingRecognition : editingOffence)) {
      // Only prompt if creating new (not editing) and has unsaved changes
      const confirmed = await showConfirm({
        title: 'Unsaved Changes',
        message: 'You have unsaved changes. Are you sure you want to close? Your draft will be preserved.',
        variant: 'warning',
        confirmText: 'Close'
      });
      if (!confirmed) {
        return;
      }
      setHasUnsavedDefinitionChanges(true);
    } else if (!hasChanges) {
      // Clear the form if no changes
      setOffenceForm({
        name: '',
        description: '',
        alternateNames: '',
        category: '',
        levels: [],
        valueIds: [],
        mentionType: 'mention',
        mentionTypeId: null
      });
      setHasUnsavedDefinitionChanges(false);
    }

    setShowOffenceModal(false);
    setEditingOffence(null);
  };

  // Category modal handlers
  const openCategoryModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setCategoryForm({
        name: category.name || '',
        icon: category.icon || 'TagIcon',
        color: category.color || '#3B82F6'
      });
    } else {
      setEditingCategory(null);
      setCategoryForm({
        name: '',
        icon: 'TagIcon',
        color: '#3B82F6'
      });
    }
    setShowCategoryModal(true);
  };

  const closeCategoryModal = () => {
    setShowCategoryModal(false);
    setEditingCategory(null);
  };

  // Level checkbox handler
  const handleLevelToggle = (level) => {
    setOffenceForm(prev => {
      const newLevels = prev.levels.includes(level)
        ? prev.levels.filter(l => l !== level)
        : [...prev.levels, level].sort();

      console.log('🔄 Level toggle:', {
        clickedLevel: level,
        previousLevels: prev.levels,
        newLevels: newLevels,
        willBePrimaryLevel: newLevels[0]
      });

      return {
        ...prev,
        levels: newLevels
      };
    });
  };

  // Value management handlers - now using value IDs
  const addValue = (value) => {
    if (!offenceForm.valueIds.includes(value.id)) {
      setOffenceForm(prev => ({
        ...prev,
        valueIds: [...prev.valueIds, value.id]
      }));
    }
    setShowValueSelector(false);
  };

  const removeValue = (valueId) => {
    setOffenceForm(prev => ({
      ...prev,
      valueIds: prev.valueIds.filter(id => id !== valueId)
    }));
  };

  // Get selected value objects from IDs
  const getSelectedValues = () => {
    return offenceForm.valueIds
      .map(id => availableValues.find(v => v.id === id))
      .filter(Boolean);
  };

  // Filter out already selected values
  const availableValuesFiltered = availableValues.filter(
    value => !offenceForm.valueIds.includes(value.id)
  );

  // Good deed level toggle handler
  const handleRecognitionLevelToggle = (level) => {
    setRecognitionForm(prev => ({
      ...prev,
      levels: prev.levels.includes(level)
        ? prev.levels.filter(l => l !== level)
        : [...prev.levels, level].sort()
    }));
  };

  // Good deed value management handlers
  const addRecognitionValue = (value) => {
    if (!recognitionForm.valueIds.includes(value.id)) {
      setRecognitionForm(prev => ({
        ...prev,
        valueIds: [...prev.valueIds, value.id]
      }));
    }
    setShowValueSelector(false);
  };

  const removeRecognitionValue = (valueId) => {
    setRecognitionForm(prev => ({
      ...prev,
      valueIds: prev.valueIds.filter(id => id !== valueId)
    }));
  };

  // Get selected recognition value objects from IDs
  const getSelectedRecognitionValues = () => {
    return recognitionForm.valueIds
      .map(id => availableValues.find(v => v.id === id))
      .filter(Boolean);
  };

  // Filter out already selected values for recognitions
  const availableRecognitionValuesFiltered = availableValues.filter(
    value => !recognitionForm.valueIds.includes(value.id)
  );

  // Category filter handlers
  const handleCategoryFilter = (categoryId) => {
    setSelectedCategory(selectedCategory === categoryId ? null : categoryId);
  };

  const clearFilters = () => {
    setSelectedCategory(null);
    setSearchQuery('');
  };

  const offenceModal = (
      <AnimatePresence>
        {showOffenceModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className={`fixed inset-0 z-[100] flex bg-black/50 backdrop-blur-sm ${
              embedded
                ? 'items-stretch justify-stretch p-0 sm:items-center sm:justify-center sm:p-4'
                : 'items-center justify-center p-4'
            }`}
            onClick={closeOffenceModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className={`flex w-full flex-col bg-base-100 shadow-xl ${
                embedded
                  ? 'h-[100dvh] max-h-[100dvh] max-w-none rounded-none sm:h-auto sm:max-h-[90vh] sm:max-w-lg sm:rounded-xl'
                  : 'max-h-[90vh] max-w-lg rounded-xl'
              }`}
              role="dialog"
              aria-modal="true"
              aria-labelledby="domain-event-editor-title"
            >
              <div className="overflow-y-auto flex-1">
              <div className={embedded ? 'p-4 sm:p-6' : 'p-6'}>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 id="domain-event-editor-title" className="text-xl font-bold sm:text-2xl">
                      {isRecognitionMode
                        ? (editingRecognition ? 'Edit Recognition' : 'Create Recognition')
                        : isCustomDomain
                        ? (editingOffence ? 'Edit Event' : 'Create Event')
                        : (editingOffence ? 'Edit Offence' : 'Create Offence')
                      }
                    </h2>
                    {hasUnsavedDefinitionChanges && !(isRecognitionMode ? editingRecognition : editingOffence) && (
                      <p className="text-sm text-warning mt-1">Draft restored</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={closeOffenceModal}
                    className="btn btn-ghost btn-circle btn-sm min-h-11 min-w-11 sm:min-h-9 sm:min-w-9"
                    aria-label="Close event editor"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={isRecognitionMode ? handleRecognitionSubmit : handleOffenceSubmit}>
                <fieldset disabled={savingDefinition} className="space-y-4">
                {/* Name (Required) - Both modes */}
                <div>
                  <label className="label">
                    <span className="label-text font-medium">
                      Name <span className="text-error">*</span>
                    </span>
                  </label>
                  <input
                    type="text"
                    placeholder={isRecognitionMode ? "e.g., Helped a classmate" : isCustomDomain ? "Name this event" : "e.g., Disrupting class"}
                    value={isRecognitionMode ? recognitionForm.name : offenceForm.name}
                    onChange={(e) => isRecognitionMode
                      ? setRecognitionForm(prev => ({ ...prev, name: e.target.value }))
                      : setOffenceForm(prev => ({ ...prev, name: e.target.value }))
                    }
                    className="input input-bordered w-full"
                    required
                  />
                </div>

                {isRecognitionMode ? (
                  <>
                    {/* Significance Levels (Required) - Recognition mode only */}
                    <div>
                      <label className="label">
                        <span className="label-text font-medium">
                          Significance Levels <span className="text-error">*</span>
                        </span>
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {RECOGNITION_LEVELS.map(level => (
                          <label key={level.id} className="cursor-pointer">
                            <input
                              type="checkbox"
                              checked={recognitionForm.levels.includes(level.id)}
                              onChange={() => handleRecognitionLevelToggle(level.id)}
                              className="checkbox checkbox-primary checkbox-sm mr-2"
                            />
                            <span className="text-sm">
                              Level {level.id} - {level.name}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Values (Optional) - Recognition mode only */}
                    <div>
                      <label className="label">
                        <span className="label-text font-medium">Values</span>
                        <span className="label-text-alt">Which values does this demonstrate?</span>
                      </label>
                      <div className="pt-4 px-4 pb-2 bg-base-50 border border-base-200 rounded-lg flex flex-col">
                        {/* Value cards area */}
                        <div className="flex flex-wrap gap-2 flex-1 mb-3">
                          {getSelectedRecognitionValues().map((value) => (
                            <div key={value.id} className="relative group">
                              <ValueCard
                                value={value}
                                variant="button"
                                className="relative"
                              />
                              <button
                                type="button"
                                onClick={() => removeRecognitionValue(value.id)}
                                className="absolute -top-1 -right-1 bg-base-200 text-base-content rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-base-300 shadow-sm"
                              >
                                <XMarkIcon className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>

                        {/* Fixed add button at bottom center */}
                        <div className="flex justify-center">
                          <div className="relative" ref={valueSelectorRef}>
                            <button
                              type="button"
                              onClick={() => setShowValueSelector(!showValueSelector)}
                              className="px-3 py-2 text-base-content/60 rounded-lg text-sm hover:bg-base-100 hover:text-base-content transition-colors flex items-center gap-1.5"
                            >
                              <PlusIcon className="w-4 h-4" />
                              Add value
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Value selector dropdown - rendered outside the container */}
                    <div className="relative">
                      <AnimatePresence>
                        {showValueSelector && availableRecognitionValuesFiltered.length > 0 && (
                          <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            transition={{ duration: 0.15, ease: 'easeOut' }}
                            className="w-80 mx-auto bg-white border border-base-200 rounded-lg shadow-lg mb-4"
                          >
                            <div className="p-3 flex flex-wrap gap-2">
                              {availableRecognitionValuesFiltered.map(value => (
                                <ValueCard
                                  key={value.id}
                                  value={value}
                                  variant="default"
                                  clickable={true}
                                  onClick={() => addRecognitionValue(value)}
                                  className="hover:bg-base-50"
                                />
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Severity Levels (Required) - legacy Discipline domain only */}
                    {!isCustomDomain && (
                <div>
                  <label className="label">
                    <span className="label-text font-medium">
                      Severity Levels <span className="text-error">*</span>
                    </span>
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {SEVERITY_LEVELS.map(level => (
                      <label key={level.id} className="cursor-pointer">
                        <input
                          type="checkbox"
                          checked={offenceForm.levels.includes(level.id)}
                          onChange={() => handleLevelToggle(level.id)}
                          className="checkbox checkbox-primary checkbox-sm mr-2"
                        />
                        <span className="text-sm">
                          Level {level.id} - {level.name}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                    )}

                {/* Category (Optional) */}
                <div>
                  <label className="label">
                    <span className="label-text font-medium">Category</span>
                  </label>
                  <select
                    value={offenceForm.category}
                    onChange={(e) => setOffenceForm(prev => ({ ...prev, category: e.target.value }))}
                    className="select select-bordered w-full"
                  >
                    <option value="">No category</option>
                    {visibleCategories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Mention Type - legacy Discipline domain only */}
                {!isCustomDomain && (
                  <div>
                    <label className="label">
                      <span className="label-text font-medium">Mention Type</span>
                      <span className="label-text-alt">Used for rule tracking</span>
                    </label>
                    <select
                      value={offenceForm.mentionTypeId || ''}
                      onChange={(e) => {
                        const selectedId = e.target.value;
                        if (selectedId) {
                          // User selected a specific mention type
                          const selectedType = mentionTypes.find(t => t.id === selectedId);
                          setOffenceForm(prev => ({
                            ...prev,
                            mentionTypeId: selectedId,
                            mentionType: selectedType?.value || 'mention'
                          }));
                        } else {
                          // User selected "Default Mention" - use the type marked as default
                          const defaultType = mentionTypes.find(t => t.isDefault);
                          setOffenceForm(prev => ({
                            ...prev,
                            mentionTypeId: (defaultType || mentionTypes[0])?.id || null,
                            mentionType: defaultType?.value || mentionTypes[0]?.value || 'mention'
                          }));
                        }
                      }}
                      className="select select-bordered w-full"
                    >
                      <option value="">Default Mention</option>
                      {mentionTypes.map(type => (
                        <option key={type.id} value={type.id}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {!isCustomDomain && (
                  <>
                {/* Alternate Names (Optional) */}
                <div>
                  <label className="label">
                    <span className="label-text font-medium">Alternate Names</span>
                    <span className="label-text-alt">Comma-separated</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., Talking out of turn, Interrupting"
                    value={offenceForm.alternateNames}
                    onChange={(e) => setOffenceForm(prev => ({ ...prev, alternateNames: e.target.value }))}
                    className="input input-bordered w-full"
                  />
                </div>

                {/* Values (Optional) */}
                <div>
                  <label className="label">
                    <span className="label-text font-medium">Values</span>
                    <span className="label-text-alt">Predefined severity options</span>
                  </label>
                  <div className="pt-4 px-4 pb-2 bg-base-50 border border-base-200 rounded-lg flex flex-col">
                    {/* Value cards area */}
                    <div className="flex flex-wrap gap-2 flex-1 mb-3">
                      {getSelectedValues().map((value) => (
                        <div key={value.id} className="relative group">
                          <ValueCard
                            value={value}
                            variant="button"
                            className="relative"
                          />
                          <button
                            type="button"
                            onClick={() => removeValue(value.id)}
                            className="absolute -top-1 -right-1 bg-base-200 text-base-content rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-base-300 shadow-sm"
                          >
                            <XMarkIcon className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>

                    {/* Fixed add button at bottom center */}
                    <div className="flex justify-center">
                      <div className="relative" ref={valueSelectorRef}>
                        <button
                          type="button"
                          onClick={() => setShowValueSelector(!showValueSelector)}
                          className="px-3 py-2 text-base-content/60 rounded-lg text-sm hover:bg-base-100 hover:text-base-content transition-colors flex items-center gap-1.5"
                        >
                          <PlusIcon className="w-4 h-4" />
                          Add value
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Value selector dropdown - rendered outside the container */}
                <div className="relative">
                  <AnimatePresence>
                    {showValueSelector && availableValuesFiltered.length > 0 && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.15, ease: 'easeOut' }}
                        className="w-80 mx-auto bg-white border border-base-200 rounded-lg shadow-lg mb-4"
                      >
                        <div className="p-3 flex flex-wrap gap-2">
                          {availableValuesFiltered.map(value => (
                            <ValueCard
                              key={value.id}
                              value={value}
                              variant="default"
                              clickable={true}
                              onClick={() => addValue(value)}
                              className="hover:bg-base-50"
                            />
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                  </>
                )}
                  </>
                )}

                {!isCustomDomain && <PhaseApplicabilityField schoolId={schoolId}
                  value={isRecognitionMode ? recognitionForm.phaseApplicability : offenceForm.phaseApplicability}
                  domainApplicability={activeDomain?.id === (isRecognitionMode ? 'recognition' : 'discipline') ? activeDomain.phaseApplicability : undefined}
                  onChange={phaseApplicability => (isRecognitionMode ? setRecognitionForm : setOffenceForm)(current => ({ ...current, phaseApplicability }))} />}
                {definitionError && <div role="alert" className="text-sm text-error">
                  <p>{definitionError.message}</p>
                  {definitionError.stale && (isRecognitionMode ? editingRecognition : editingOffence) && <button type="button" className="btn btn-ghost btn-sm"
                    onClick={async () => {
                      setSavingDefinition(true);
                      try {
                        const item = isRecognitionMode ? editingRecognition : editingOffence;
                        const snapshot = await getDoc(doc(db, 'schools', schoolId, isRecognitionMode ? 'recognitions' : 'offences', item.id));
                        if (!snapshot.exists() || snapshot.data().retired || (isRecognitionMode && !isRecognitionDefinition(snapshot.data()))) {
                          throw new Error('This definition is no longer available.');
                        }
                        openOffenceModal({ ...snapshot.data(), id: snapshot.id });
                      } catch (error) { setDefinitionError({ message: error.message, stale: true }); }
                      finally { setSavingDefinition(false); }
                    }}>Load saved {isRecognitionMode ? 'recognition' : 'offence'}</button>}
                </div>}

                {/* Description (Optional) - Both modes */}
                <div>
                  <label className="label">
                    <span className="label-text font-medium">Description</span>
                  </label>
                  <textarea
                    placeholder={isRecognitionMode ? "Additional details about this recognition..." : isCustomDomain ? "What does this event capture?" : "Additional details about this offence..."}
                    value={isRecognitionMode ? recognitionForm.description : offenceForm.description}
                    onChange={(e) => isRecognitionMode
                      ? setRecognitionForm(prev => ({ ...prev, description: e.target.value }))
                      : setOffenceForm(prev => ({ ...prev, description: e.target.value }))
                    }
                    className="textarea textarea-bordered w-full"
                    rows={3}
                  />
                </div>

                {/* Form Actions */}
                <div className="flex items-center justify-between pt-6">
                  <div>
                    {isRecognitionMode ? (
                      editingRecognition && (
                        <button
                          type="button"
                          onClick={() => handleRecognitionDelete(editingRecognition)}
                          className="btn btn-error btn-outline"
                        >
                          <TrashIcon className="w-4 h-4" />
                          Retire
                        </button>
                      )
                    ) : (
                      editingOffence && (
                        <button
                          type="button"
                          onClick={() => handleOffenceDelete(editingOffence)}
                          className="btn btn-error btn-outline"
                        >
                          <TrashIcon className="w-4 h-4" />
                          {isCustomDomain ? 'Delete' : 'Retire'}
                        </button>
                      )
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={closeOffenceModal}
                      className="btn btn-ghost"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                    >
                      {isRecognitionMode
                        ? (editingRecognition ? 'Update' : 'Create') + ' Recognition'
                        : isCustomDomain
                        ? (editingOffence ? 'Update' : 'Create') + ' Event'
                        : (editingOffence ? 'Update' : 'Create') + ' Offence'
                      }
                    </button>
                  </div>
                </div>
              </fieldset>
                </form>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
      </AnimatePresence>
  );

  const categoryModal = (
      <AnimatePresence>
        {showCategoryModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className={`fixed inset-0 z-[100] flex bg-black/50 backdrop-blur-sm ${
              embedded
                ? 'items-stretch justify-stretch p-0 sm:items-center sm:justify-center sm:p-4'
                : 'items-center justify-center p-4'
            }`}
            onClick={closeCategoryModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className={`w-full overflow-y-auto bg-base-100 shadow-xl ${
                embedded
                  ? 'h-[100dvh] max-h-[100dvh] max-w-none rounded-none sm:h-auto sm:max-h-[90vh] sm:max-w-md sm:rounded-xl'
                  : 'max-w-md rounded-xl'
              }`}
              role="dialog"
              aria-modal="true"
              aria-labelledby="domain-category-editor-title"
            >
              <div className={embedded ? 'p-4 sm:p-6' : 'p-6'}>
                <div className="flex items-center justify-between mb-6">
                  <h2 id="domain-category-editor-title" className="text-xl font-bold sm:text-2xl">
                    {editingCategory ? 'Edit Category' : 'Create Category'}
                  </h2>
                  <button
                    type="button"
                    onClick={closeCategoryModal}
                    className="btn btn-ghost btn-circle btn-sm min-h-11 min-w-11 sm:min-h-9 sm:min-w-9"
                    aria-label="Close category editor"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={handleCategorySubmit} className="space-y-4">
                {/* Name */}
                <div>
                  <label className="label">
                    <span className="label-text font-medium">
                      Name <span className="text-error">*</span>
                    </span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., Behavioral"
                    value={categoryForm.name}
                    onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                    className="input input-bordered w-full"
                    required
                  />
                </div>

                {/* Icon */}
                <div>
                  <label className="label">
                    <span className="label-text font-medium">Icon</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowIconPicker(true)}
                    className="btn btn-outline justify-start space-x-2 w-full"
                  >
                    {getIconByName(categoryForm.icon, "w-5 h-5")}
                    <span>{categoryForm.icon.replace('Icon', '')}</span>
                    <PhotoIcon className="w-4 h-4 ml-auto opacity-60" />
                  </button>
                </div>

                {/* Color */}
                <div>
                  <label className="label">
                    <span className="label-text font-medium">Color</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowColorPicker(true)}
                    className="btn btn-outline justify-start space-x-2 w-full"
                  >
                    <div
                      className="w-5 h-5 rounded border border-base-300"
                      style={{ backgroundColor: categoryForm.color }}
                    />
                    <span className="font-mono">{categoryForm.color}</span>
                  </button>
                </div>

                {/* Form Actions */}
                <div className="flex items-center justify-between pt-6">
                  <div>
                    {editingCategory && (
                      <button
                        type="button"
                        onClick={() => handleCategoryDelete(editingCategory)}
                        className="btn btn-error btn-outline"
                      >
                        <TrashIcon className="w-4 h-4" />
                        Delete
                      </button>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={closeCategoryModal}
                      className="btn btn-ghost"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                    >
                      {editingCategory ? 'Update' : 'Create'} Category
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </motion.div>
        </motion.div>
      )}
      </AnimatePresence>
  );

  const body = (
    <>
      {typeof document !== 'undefined' && createPortal(offenceModal, document.body)}
      {typeof document !== 'undefined' && createPortal(categoryModal, document.body)}

      {/* Icon Picker Modal */}
      {showIconPicker && (
        <IconPicker
          selectedIcon={categoryForm.icon}
          onIconSelect={(iconName) => {
            setCategoryForm(prev => ({ ...prev, icon: iconName }));
            setShowIconPicker(false);
          }}
          onClose={() => setShowIconPicker(false)}
        />
      )}

      {/* Color Picker Modal */}
      {showColorPicker && (
        <ColorPicker
          selectedColor={categoryForm.color}
          onColorSelect={(color) => {
            setCategoryForm(prev => ({ ...prev, color: color }));
            setShowColorPicker(false);
          }}
          onClose={() => setShowColorPicker(false)}
        />
      )}

      <div className={embedded ? '' : 'container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl mt-8'}>
        {/* Header — page-scale */}
        {!embedded && (
          <div className={`${CSS_CLASSES.CARD_ELEVATED} mb-6`}>
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
              <div className="flex-1">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mb-2">
                  <h1 className="text-2xl sm:text-3xl font-bold text-base-content">
                    {isRecognitionMode ? 'Recognitions Manager' : 'Offences Manager'}
                  </h1>
                  {/* Recognition Mode Toggle */}
                  <label className="flex items-center gap-2 cursor-pointer flex-shrink-0">
                    <span className={`text-sm ${!isRecognitionMode ? 'text-base-content' : 'text-base-content/50'}`}>
                      Discipline
                    </span>
                    <input
                      type="checkbox"
                      className="toggle toggle-sm toggle-success"
                      checked={isRecognitionMode}
                      onChange={(e) => handleToggleMode(e.target.checked)}
                    />
                    <span className={`text-sm ${isRecognitionMode ? 'text-base-content' : 'text-base-content/50'}`}>
                      Recognition
                    </span>
                  </label>
                </div>
                <p className="text-base-content/70">
                  {isRecognitionMode
                    ? 'Manage recognitions and positive behaviors linked to values'
                    : 'Manage offences across four severity levels'}
                </p>
              </div>
              <div className="flex flex-wrap gap-2 flex-shrink-0">
                {!isRecognitionMode && (
                  <button
                    onClick={() => openCategoryModal()}
                    className={`${CSS_CLASSES.BUTTON_BASE_WITH_SHADOW} btn-secondary whitespace-nowrap`}
                  >
                    <TagIcon className="w-5 h-5" />
                    <span className="hidden sm:inline">Create Category</span>
                    <span className="sm:hidden">Category</span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => openOffenceModal()}
                  className={`${CSS_CLASSES.BUTTON_BASE_WITH_SHADOW} btn-primary whitespace-nowrap`}
                >
                  <PlusIcon className="w-5 h-5" />
                  <span className="hidden sm:inline">
                    {isRecognitionMode ? 'Create Recognition' : 'Create Offence'}
                  </span>
                  <span className="sm:hidden">
                    {isRecognitionMode ? 'Recognition' : 'Offence'}
                  </span>
                </button>
              </div>
            </div>

            {/* Search and Filters */}
            <div className="mt-6 space-y-4">
              {/* Search Bar */}
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-base-content/50" />
                <input
                  type="text"
                  placeholder={isRecognitionMode ? "Search recognitions..." : "Search offences..."}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="input input-bordered w-full pl-10"
                />
                {(searchQuery || selectedCategory) && (
                  <button
                    onClick={clearFilters}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-base-content/50 hover:text-base-content"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                )}
              </div>

            {/* Category Filter Pills - Only in Discipline Mode */}
            {!isRecognitionMode && (
              <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-base-content/70">Filter:</span>

              {/* All filter */}
              <CategoryTag
                category={{
                  id: 'all',
                  name: 'All',
                  icon: null,
                  color: '#6B7280'
                }}
                isSelected={!selectedCategory || selectedCategory === 'all'}
                onClick={() => setSelectedCategory(null)}
                count={phaseOffences.length}
                size="small"
                showIcon={false}
              />

              {/* Separator */}
              {visibleCategories.length > 0 && (
                <div className="w-px h-6 bg-base-300 mx-1" />
              )}

              {/* Category filters */}
              {visibleCategories.length > 0 ? (
                <>
                  {visibleCategories.map(category => (
                    <div key={category.id} className="relative group">
                      <CategoryTag
                        category={category}
                        isSelected={selectedCategory === category.id}
                        onClick={() => handleCategoryFilter(category.id)}
                        count={phaseOffences.filter(o => o.categoryId === category.id).length}
                        size="small"
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openCategoryModal(category);
                        }}
                        className="absolute -top-1 -right-1 bg-base-100 border border-base-300 rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-base-200 shadow-sm"
                      >
                        <PencilIcon className="w-3 h-3" />
                      </button>
                    </div>
                  ))}

                  {/* Separator before uncategorized */}
                  <div className="w-px h-6 bg-base-300 mx-1" />

                  {/* Uncategorized filter */}
                  <CategoryTag
                    category={{
                      id: 'uncategorized',
                      name: 'Uncategorized',
                      icon: 'QuestionMarkCircleIcon',
                      color: '#9CA3AF'
                    }}
                    isSelected={selectedCategory === 'uncategorized'}
                    onClick={() => handleCategoryFilter('uncategorized')}
                    count={phaseOffences.filter(o => !o.categoryId).length}
                    size="small"
                  />
                </>
              ) : (
                <>
                  <span className="text-sm text-base-content/50 italic ml-2">
                    No categories created yet. Click "Create Category" to add one.
                  </span>

                  {/* Still show uncategorized even if no categories exist */}
                  {phaseOffences.filter(o => !o.categoryId).length > 0 && (
                    <>
                      <div className="w-px h-6 bg-base-300 mx-1" />
                      <CategoryTag
                        category={{
                          id: 'uncategorized',
                          name: 'Uncategorized',
                          icon: 'QuestionMarkCircleIcon',
                          color: '#9CA3AF'
                        }}
                        isSelected={selectedCategory === 'uncategorized'}
                        onClick={() => handleCategoryFilter('uncategorized')}
                        count={phaseOffences.filter(o => !o.categoryId).length}
                        size="small"
                      />
                    </>
                  )}
                </>
              )}
              </div>
            )}
          </div>
        </div>
        )}

        {/* Header — embedded (compact) */}
        {embedded && (
          <EmbeddedToolbar
            leading={
              isCustomDomain || modeLocked ? null : (
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <span className={`text-xs ${!isRecognitionMode ? 'text-base-content' : 'text-base-content/50'}`}>Discipline</span>
                  <input
                    type="checkbox"
                    className="toggle toggle-xs toggle-success"
                    checked={isRecognitionMode}
                    onChange={(e) => handleToggleMode(e.target.checked)}
                  />
                  <span className={`text-xs ${isRecognitionMode ? 'text-base-content' : 'text-base-content/50'}`}>Recognition</span>
                </label>
              )
            }
            actions={
              <>
                {!isRecognitionMode && (
                  <button
                    type="button"
                    onClick={() => openCategoryModal()}
                    className="btn btn-secondary btn-xs gap-1"
                  >
                    <TagIcon className="w-3.5 h-3.5" />
                    Category
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => openOffenceModal()}
                  className="btn btn-primary btn-xs gap-1"
                >
                  <PlusIcon className="w-3.5 h-3.5" />
                  {isCustomDomain ? 'Event' : isRecognitionMode ? 'Recognition' : 'Offence'}
                </button>
              </>
            }
            search={{
              placeholder: isRecognitionMode ? 'Search recognitions…' : 'Search offences…',
              value: searchQuery,
              onChange: (e) => setSearchQuery(e.target.value),
              onClear: searchQuery || selectedCategory ? clearFilters : null,
            }}
            filters={
              !isRecognitionMode && visibleCategories.length > 0 ? (
                <div className="flex flex-wrap items-center gap-1">
                  <CategoryTag
                    category={{ id: 'all', name: 'All', icon: null, color: '#6B7280' }}
                    isSelected={!selectedCategory || selectedCategory === 'all'}
                    onClick={() => setSelectedCategory(null)}
                    count={filteredOffences.length}
                    size="small"
                    showIcon={false}
                  />
                  {visibleCategories.map((category) => (
                    <CategoryTag
                      key={category.id}
                      category={category}
                      isSelected={selectedCategory === category.id}
                      onClick={() => handleCategoryFilter(category.id)}
                      count={filteredOffences.filter((o) => o.categoryId === category.id).length}
                      size="small"
                    />
                  ))}
                  {phaseOffences.filter((o) => !o.categoryId).length > 0 && (
                    <CategoryTag
                      category={{ id: 'uncategorized', name: 'Uncat.', icon: 'QuestionMarkCircleIcon', color: '#9CA3AF' }}
                      isSelected={selectedCategory === 'uncategorized'}
                      onClick={() => handleCategoryFilter('uncategorized')}
                      count={phaseOffences.filter((o) => !o.categoryId).length}
                      size="small"
                    />
                  )}
                </div>
              ) : null
            }
          />
        )}

        {/* Content Display */}
        {isRecognitionMode ? (
          /* Recognitions Display - Grouped by Levels */
          <div className="space-y-4">
            {RECOGNITION_LEVELS.map(level => {
              const levelRecognitions = getRecognitionsByLevel(level.id);

              return (
                <div key={level.id} className="card bg-base-100 border border-base-300">
                  <div className={embedded ? 'p-3' : 'p-4'}>
                    {/* Level Header - Horizontal */}
                    <div className={`flex items-center justify-between border-b border-base-300 ${embedded ? 'mb-2 pb-2' : 'mb-4 pb-4'}`}>
                      <div className={`flex items-center min-w-0 ${embedded ? 'gap-2' : 'gap-4'}`}>
                        <h3 className={embedded ? 'font-semibold text-sm whitespace-nowrap' : 'font-bold text-lg'}>Level {level.id}</h3>
                        <div className={`flex items-center min-w-0 ${embedded ? 'gap-1' : 'gap-2'}`}>
                          <h4 className={embedded ? 'font-medium text-sm truncate' : 'font-semibold'}>{level.name}</h4>
                          {!embedded && <span className="text-sm opacity-75">• {level.description}</span>}
                          <span className={`font-medium text-success whitespace-nowrap ${embedded ? 'text-xs' : 'text-sm'}`}>
                            • {level.points} {level.points === 1 ? 'pt' : 'pts'}
                          </span>
                        </div>
                      </div>
                      <span className={`badge badge-success ${embedded ? 'badge-xs' : 'badge-sm'}`}>
                        {levelRecognitions.length}
                      </span>
                    </div>

                    {/* Recognitions List - Full Width */}
                    <div className="space-y-2 min-h-[80px]">
                      <AnimatePresence>
                        {levelRecognitions.map(recognition => (
                          <motion.div
                            key={recognition.id}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 10 }}
                            className={`flex items-center justify-between rounded bg-base-200/50 hover:bg-base-200 cursor-pointer transition-colors ${embedded ? 'px-3 py-2' : 'p-3'}`}
                            onClick={() => openOffenceModal(recognition)}
                          >
                            <div className={`flex items-center min-w-0 ${embedded ? 'gap-2' : 'gap-3'}`}>
                              <span className={`font-medium truncate ${embedded ? 'text-sm' : ''}`}>{recognition.name}</span>
                              {recognition.description && !embedded && (
                                <span className="text-sm text-base-content/60">
                                  {recognition.description}
                                </span>
                              )}
                            </div>
                            {/* Show value badges */}
                            <div className="flex gap-1">
                              {recognition.valueIds?.map(valueId => {
                                const value = availableValues.find(v => v.id === valueId);
                                return value ? (
                                  <ValueCard
                                    key={valueId}
                                    value={value}
                                    variant="button"
                                  />
                                ) : null;
                              })}
                            </div>
                          </motion.div>
                        ))}
                      </AnimatePresence>

                      {/* Empty State */}
                      {levelRecognitions.length === 0 && (
                        <div className="w-full text-center py-6 text-base-content/50">
                          <p className="text-sm">No recognitions in this level</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : isCustomDomain ? (
          /* Custom domain Events - flat list (no severity levels). Use existing +Event button + modal. */
          <div className="card bg-base-100 border border-base-300">
            <div className={embedded ? 'p-3' : 'p-4'}>
              <div className="space-y-2 min-h-[80px]">
                <AnimatePresence>
                  {filteredOffences.map(offence => (
                    <motion.div
                      key={offence.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 10 }}
                      className="w-full"
                    >
                      <OffenceTag
                        offence={offence}
                        category={getCategoryForOffence(offence.categoryId)}
                        onClick={() => openOffenceModal(offence)}
                        variant="compact"
                        showLevels={false}
                        showCategory={true}
                        embedded={embedded}
                      />
                    </motion.div>
                  ))}
                </AnimatePresence>

                {filteredOffences.length === 0 && (
                  <div className="w-full text-center py-6 text-base-content/50">
                    <p className="text-sm">No events yet</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          /* Severity Levels - Horizontal Cards */
          <div className="space-y-4">
            {SEVERITY_LEVELS.map(level => {
              const levelOffences = getOffencesByLevel(level.id);

              return (
                <div key={level.id} className="card bg-base-100 border border-base-300">
                  <div className={embedded ? 'p-3' : 'p-4'}>
                    {/* Level Header - Horizontal */}
                    <div className={`flex items-center justify-between border-b border-base-300 ${embedded ? 'mb-2 pb-2' : 'mb-4 pb-4'}`}>
                      <div className={`flex items-center min-w-0 ${embedded ? 'gap-2' : 'gap-4'}`}>
                        <h3 className={embedded ? 'font-semibold text-sm whitespace-nowrap' : 'font-bold text-lg'}>Level {level.id}</h3>
                        <div className={`flex items-center min-w-0 ${embedded ? 'gap-1' : 'gap-2'}`}>
                          <h4 className={embedded ? 'font-medium text-sm truncate' : 'font-semibold'}>{level.name}</h4>
                          {!embedded && <span className="text-sm opacity-75">• {level.description}</span>}
                        </div>
                      </div>
                      <span className={`badge badge-neutral ${embedded ? 'badge-xs' : 'badge-sm'}`}>
                        {levelOffences.length}
                      </span>
                    </div>

                    {/* Offences List - Full Width */}
                    <div className="space-y-2 min-h-[80px]">
                      <AnimatePresence>
                        {levelOffences.map(offence => (
                          <motion.div
                            key={offence.id}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 10 }}
                            className="w-full"
                          >
                            <OffenceTag
                              offence={offence}
                              category={getCategoryForOffence(offence.categoryId)}
                              onClick={() => openOffenceModal(offence)}
                              variant="compact"
                              showLevels={false}
                              showCategory={true}
                              embedded={embedded}
                            />
                          </motion.div>
                        ))}
                      </AnimatePresence>

                      {/* Empty State */}
                      {levelOffences.length === 0 && (
                        <div className="w-full text-center py-6 text-base-content/50">
                          <ExclamationTriangleIcon className="w-6 h-6 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">No offences in this level</p>
                          {selectedCategory && (
                            <p className="text-xs mt-1">Try clearing the category filter</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}


      </div>

      {/* Confirm Dialog */}
        <ConfirmDialog {...confirmDialog} />
      </>
  );

  if (embedded) {
    if (loading) return <OffencesManagerSkeleton />;
    return body;
  }

  return (
    <StandardPage loading={loading} skeleton={<OffencesManagerSkeleton />}>
      {!embedded && <div className="flex justify-end px-4"><PhaseScopeSelector /></div>}
      {body}
    </StandardPage>
  );
}
